import React, { useState, useEffect } from 'react'
import { MinSOCChart } from './MinSOCChart'
import { Services } from '@/services/services'
import { CurrentSession } from '@/services/repository/session'
import { useTranslations } from '@/hooks/useTranslations'
import { SearchCriteria, SearchCriteriaParams } from '@/pages/communal/statistics/components/common/searchCriteria'
import { mapBuckets } from '../common/labels'
import { HelpPopupButton } from '@/popups/helppopup/help'
import { formatDate } from '@/common/time'
import { MachineType } from '@/services/model/machine'
import { StaticPaginatedTable, StaticPaginatedTableState } from '@/components/search/StaticPaginatedTable'
import { MachineName } from '@/components/machine'
import { HitText } from '@/components/hit/HitText'
import { SOCText } from '@/components/soc/SOCText'
import { Order, OrderManager } from '@/components/order/Order'

type MinSOCPopupProps = {
  services: Services
  session: CurrentSession
  criteria: SearchCriteria
  bucketFor: Date
  goToUserPage: (userId: string) => void
  goToMachinePage: (machineId: string) => void
  onClose: () => void
}

interface Machine {
  id: string
  imei: string
  internalNumber: string
  type: MachineType
}

interface User {
  id: string
  username: string
}

interface MachineHit {
  imei: string
  time: string
  soc: number | null
  machine: Machine
  user: User
}

const MinSOCPopupOrderCols = ['machine.internalNumber', 'time', 'soc', 'user.username']
const MinSOCPopupDefaultOrder: Order = {
  key: 'soc',
  asc: true
}

export const MinSOCPopupBody = ({ services, session, criteria, bucketFor, goToUserPage, goToMachinePage, onClose }: MinSOCPopupProps) => {
  const translates = useTranslations(services, session)
  const [response, setResponse] = useState<MachineHit[] | undefined>()
  const [listState, setListState] = useState<StaticPaginatedTableState>({
    pageNumber: 1,
    pageSize: 10
  })
  const [orderManager, setOrderManager] = useState<OrderManager>(() => new OrderManager(
    MinSOCPopupOrderCols,
    MinSOCPopupDefaultOrder,
    (order: Order) => {
      setOrderManager((current) => current.newManager(order))
      setListState((current) => ({ ...current, pageNumber: 1 }))
    }
  ))

  useEffect(() => {
    setResponse(undefined)
    services.session
      .fetchWithToken(`/api/communal/statistics/machines/bucket/soc?bucketFor=${bucketFor.toISOString()}&${SearchCriteriaParams(criteria)}`)
      .then((r) => r.json())
      .then((data) => {
        setResponse(data)
        setListState({
          pageNumber: 1,
          pageSize: 10
        })
      })
  }, [services.session, criteria])

  if (translates === undefined || !response) {
    return <div className='spinner'></div>
  }

  const headerRow = (key: string, translationKey: string) => <div className='hcol' onClick={() => orderManager.click(key)}>
    {translates.for(translationKey)}
    {orderManager.orderInfoElement(key)}
  </div>

  const orderedResponse = orderManager.orderListByKey(response)

  return (
    <div>
      <h3>{formatDate(new Date(bucketFor))}</h3>

      <StaticPaginatedTable<MachineHit>
        data={orderedResponse}
        parentState={listState}
        setParentState={setListState}
        header={() => <>
          {headerRow('machine.internalNumber', 'elements.table.minsocpop.machine')}
          {headerRow('time', 'elements.table.minsocpop.time')}
          {headerRow('soc', 'elements.table.minsocpop.min_soc')}
          {headerRow('user.username', 'elements.table.minsocpop.user')}
        </>}
        row={(row) => {
          return <>
            <div className={`col`} onClick={() => {
              goToMachinePage(row.machine.id)
              onClose()
            }}>{row.machine ? <MachineName row={row.machine} /> : '-'}</div>
            <div className={`col`}>{row.time ? formatDate(new Date(row.time)) : '-'}</div>
            <div className={`col`}>
              {row.soc === null ? '-' : <SOCText value={row.soc}>{row.soc.toFixed(2)}%</SOCText>}
            </div>
            <div className={`col`} onClick={() => {
              goToUserPage(row.user.id)
              onClose()
            }}>{row.user?.username || '-'}</div>
          </>
        }}
        translates={session.translates}
      />
    </div>
  )
}
